/**
 * Site Selector Component
 *
 * Compact dropdown for the app header to switch between sites.
 * Three rendering modes:
 * 1. Corporate user: "All Sites" + dropdown of all sites
 * 2. Multi-site user: dropdown of assigned sites only — NO "All Sites", which
 *    the backend rejects for them (see the comment on the option below)
 * 3. Single-site user: static label (non-interactive)
 *
 * Hides entirely if no sites available or user not logged in.
 */

import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Globe, Loader2 } from 'lucide-react';
import { useSite } from '../context/SiteContext';

const SiteSelector = () => {
  const {
    activeSiteId,
    activeSiteName,
    isCorpUser,
    availableSites,
    switching,
    switchSite,
    initialized
  } = useSite();

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render until initialized
  if (!initialized) return null;

  // Hide if no sites exist
  if (availableSites.length === 0) return null;

  // Single-site, non-corporate user: show static label
  if (!isCorpUser && availableSites.length === 1) {
    return (
      <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
        <MapPin className="w-3.5 h-3.5" />
        <span className="font-medium truncate max-w-[140px]">
          {availableSites[0].site_name}
        </span>
      </div>
    );
  }

  // A site-scoped user with nothing selected is not looking at all sites —
  // they are looking at nothing until they pick one, and the label should say
  // so rather than name a scope they are not allowed to have.
  const displayLabel = activeSiteName || (isCorpUser ? 'All Sites' : 'Select a site');

  const handleSelect = async (siteId) => {
    setOpen(false);
    if (siteId === activeSiteId) return;
    await switchSite(siteId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
          activeSiteId
            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {switching ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : activeSiteId ? (
          <MapPin className="w-3.5 h-3.5" />
        ) : (
          <Globe className="w-3.5 h-3.5" />
        )}
        <span className="font-medium truncate max-w-[140px]">{displayLabel}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-64 overflow-y-auto">
          {/* "All Sites" — CORPORATE users only.
              It used to be offered to anyone with 2+ sites, including
              site-scoped users. For them the server rejects it: every
              dashboard read runs resolve_dashboard_site_id(strict=True) and
              answers 400 "site_id required", because dropping the site filter
              would show a site-scoped user every site in the tenant. So the
              option was a guaranteed broken page — a chart reading "Failed to
              load chart" and KPI cards reading "no data". */}
          {isCorpUser && (
            <button
              onClick={() => handleSelect(null)}
              className={`w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                !activeSiteId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}
            >
              <Globe className="w-4 h-4 flex-shrink-0" />
              <span>All Sites</span>
            </button>
          )}

          {isCorpUser && availableSites.length > 0 && (
            <div className="border-t border-gray-100 my-1" />
          )}

          {/* Site list */}
          {availableSites.map((site) => (
            <button
              key={site.site_id}
              onClick={() => handleSelect(site.site_id)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                activeSiteId === site.site_id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2 min-w-0">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{site.site_name}</span>
              </div>
              {site.role && (
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                  {site.role}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SiteSelector;
